import React from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, Search } from "lucide-react";

import {
  ALERT_PLACES, ALERT_TYPES, ALL_ALERT_KINDS, alertCoverage, alertDriverChoices, alertKindsSummary, driverAlertKinds, driverAlertStatus,
  withDriverRule,
} from "../../utils/geotabLiveAlerts.js";
import { CheckBox, SwitchItem } from "./DispatchDisplayMenuControls.jsx";

const SUMMARY_CLASS = {
  default: "text-accent ring-1 ring-inset ring-accent",
  custom: "bg-accent text-white",
  none: "bg-slate-200 text-slate-700",
};

function BackHeader({ title, right, onBack }) {
  const backRef = React.useRef(null);
  React.useEffect(() => { backRef.current?.focus(); }, []);
  return (
    <div className="flex items-center gap-1.5 px-1.5 pb-2 pt-1.5 text-[0.8rem] font-extrabold text-slate-900">
      <button ref={backRef} type="button" role="menuitem" tabIndex={-1} data-keep-menu-open aria-label="Retour" onClick={onBack}
        className="grid h-6 w-6 shrink-0 place-items-center rounded-[4px] border border-slate-300 text-slate-700 hover:bg-slate-50">
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="min-w-0 truncate">{title}</span>
      <span className="ml-auto shrink-0 text-[0.68rem]">{right}</span>
    </div>
  );
}

const coverageText = (coverage) => (coverage.total ? `${coverage.withAlerts} sur ${coverage.total} avec avis` : "");

// Tête du menu, jamais repliée : tous les chauffeurs ont leurs avis, ou un encadré orange dit qui n'en a pas.
export function AlertDriversHeadline({ display, drivers, onOpen }) {
  const coverage = alertCoverage(display, drivers);
  if (!coverage.limited) {
    return (
      <div role="group" aria-label="Avis Geotab · chauffeurs" className="dispatch-header-menu-section" data-dispatch-display-section="drivers">
        <div className="dispatch-header-menu-label">Avis Geotab · chauffeurs</div>
        <button type="button" role="menuitem" tabIndex={-1} data-keep-menu-open data-dispatch-display-drivers-open
          className="dispatch-header-menu-action" onClick={onOpen}>
          <span className="min-w-0 flex-1 flex-col items-start! gap-0!">
            <span className="font-bold text-content-ink">Tous les chauffeurs</span>
            <span className="text-[0.72rem] font-normal text-slate-700">Chacun reçoit les avis choisis plus bas</span>
          </span>
          <span className="shrink-0 whitespace-nowrap text-[0.72rem] font-extrabold text-accent">
            {coverage.total ? `${coverage.total} sur ${coverage.total}` : "Tous"}<ChevronRight className="h-3.5 w-3.5" />
          </span>
        </button>
      </div>
    );
  }
  return (
    <div role="group" aria-label="Avis Geotab · chauffeurs" className="dispatch-header-menu-section" data-dispatch-display-section="drivers" data-limited>
      <div className="dispatch-header-menu-label text-[#c2410c]!">Avis Geotab · chauffeurs</div>
      <button type="button" role="menuitem" tabIndex={-1} data-keep-menu-open data-dispatch-display-drivers-open onClick={onOpen}
        className="mx-1 mb-1 block w-[calc(100%-0.5rem)] rounded-[4px] border border-[#f1d2c2] border-l-4 border-l-[#c2410c] bg-white px-2.5 py-2 text-left hover:bg-slate-50">
        <span className="flex items-center gap-2 text-[0.84rem] font-extrabold text-slate-900">
          <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0 text-[#c2410c]" strokeWidth={2.5} />
          Avis limités : {coverage.withAlerts} chauffeur{coverage.withAlerts > 1 ? "s" : ""} sur {coverage.total}
          <span className="ml-auto inline-flex items-center gap-1 text-[0.72rem] text-accent">Voir <ChevronRight className="h-3.5 w-3.5" /></span>
        </span>
        {coverage.none.length ? (
          <span className="mt-1 block text-[0.72rem] leading-5 text-slate-700">
            <b className="text-[#c2410c]">Aucun avis :</b> {coverage.none.map((driver) => driver.driver).join(", ")}
          </span>
        ) : null}
        {coverage.custom.length ? (
          <span className="block text-[0.72rem] leading-5 text-slate-700">
            <b className="text-slate-900">À part :</b> {coverage.custom.map((driver) => `${driver.driver} (${driver.summary})`).join(", ")}
          </span>
        ) : null}
      </button>
    </div>
  );
}

// Les chauffeurs du jour avec leur état en direct et leurs avis ; un clic ouvre le réglage d'un chauffeur.
export function AlertDriversView({ display, onChange, live, onBack, onDriver }) {
  const [query, setQuery] = React.useState("");
  const refreshDrivers = live?.refreshDrivers;
  React.useEffect(() => { refreshDrivers?.(); }, [refreshDrivers]);
  const coverage = alertCoverage(display, live?.drivers);
  const needle = query.trim().toLocaleLowerCase("fr");
  const choices = alertDriverChoices(live?.drivers, display.alert_driver_rules)
    .filter((choice) => !needle || `${choice.driver} ${choice.truck}`.toLocaleLowerCase("fr").includes(needle));
  return (
    <div role="group" aria-label="Chauffeurs" data-dispatch-display-drivers>
      <BackHeader title="Chauffeurs" onBack={onBack}
        right={<span className={coverage.limited ? "text-[#c2410c]" : "text-accent"}>{coverageText(coverage)}</span>} />
      <label className="mx-1.5 mb-1.5 flex h-[30px] items-center gap-1.5 rounded-[4px] border border-slate-300 px-2 text-slate-500">
        <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Chercher un chauffeur ou un camion"
          aria-label="Chercher un chauffeur ou un camion" className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.74rem] text-slate-900 outline-none placeholder:text-slate-500" />
      </label>
      <SwitchItem option="alert_others_off" checked={Boolean(display.alert_others_off)} onChange={onChange}
        label="Aucun avis pour les autres" hint="Seuls les chauffeurs réglés à part reçoivent des avis" />
      <div className="px-2 pb-1.5 text-[0.72rem] text-slate-700">Par défaut, chaque chauffeur a tous les avis choisis dans le menu.</div>
      <div className="border-t border-slate-100">
        {choices.map((choice) => {
          const status = driverAlertStatus(display, choice.driver_key);
          const summary = status === "default" ? "Tous les avis" : alertKindsSummary(driverAlertKinds(display, choice.driver_key));
          return (
            <button key={choice.driver_key} type="button" role="menuitem" tabIndex={-1} data-keep-menu-open data-alert-driver={choice.driver_key}
              className="dispatch-header-menu-action" onClick={() => onDriver(choice)}>
              <span className="min-w-0 flex-1 flex-col items-start! gap-0!">
                <span className={choice.absent ? "text-slate-500" : ""}>
                  {choice.driver}{choice.truck ? <span className="ml-1.5 text-[0.66rem] font-extrabold text-slate-700">{choice.truck}</span> : null}
                </span>
                <span className="block! max-w-full truncate text-[0.68rem] font-normal text-slate-700">{choice.absent ? "Pas de tournée aujourd'hui" : choice.state || "État inconnu"}</span>
              </span>
              <span className={`shrink-0 whitespace-nowrap rounded-[4px] px-1.5 py-0.5 text-[0.68rem] font-extrabold ${SUMMARY_CLASS[status]}`} data-alert-driver-status={status}>{summary}</span>
              <ChevronRight className="h-3 w-3 shrink-0 text-slate-500" />
            </button>
          );
        })}
        {!choices.length ? <p className="m-0 px-2 py-3 text-[0.72rem] text-slate-700">{needle ? "Aucun chauffeur trouvé." : live?.error || "Lecture de Geotab…"}</p> : null}
      </div>
    </div>
  );
}

// Avis d'un chauffeur, lieu par lieu : partir des avis qu'il reçoit, puis cocher ou décocher.
export function AlertDriverEditor({ display, onChange, driver, onBack }) {
  const status = driverAlertStatus(display, driver.driver_key);
  const kinds = driverAlertKinds(display, driver.driver_key);
  const setKinds = (next) => onChange("alert_driver_rules", withDriverRule(display, driver, next));
  const toggle = (kind) => setKinds(kinds.includes(kind) ? kinds.filter((item) => item !== kind) : [...kinds, kind]);
  const gates = { arrival: display.alert_arrival, departure: display.alert_departure };
  const label = { default: "Par défaut", custom: "À part", none: "Aucun avis" }[status];
  return (
    <div role="group" aria-label={`Avis de ${driver.driver}`} data-dispatch-display-driver={driver.driver_key}>
      <BackHeader onBack={onBack} right={<span className={status === "default" ? "text-accent" : "text-[#c2410c]"}>{label}</span>}
        title={<>{driver.driver}{driver.truck ? <span className="ml-1.5 text-[0.66rem] text-slate-700">{driver.truck}</span> : null}</>} />
      {driver.state ? <div className="truncate px-2 pb-1.5 text-[0.72rem] text-slate-700">{driver.state}</div> : null}
      <div className="mx-2 mb-2 grid grid-cols-[minmax(0,1fr)_4.5rem_4.5rem] overflow-hidden rounded-[4px] border border-slate-200 text-[0.78rem]">
        <span className="border-b border-slate-100 px-2 py-1.5 text-[0.64rem] font-extrabold uppercase tracking-[0.08em] text-slate-900">Lieu</span>
        {ALERT_TYPES.map(([type, typeLabel]) => (
          <span key={type} className="border-b border-slate-100 py-1.5 text-center text-[0.64rem] font-extrabold uppercase tracking-[0.08em] text-slate-900">{typeLabel}</span>
        ))}
        {ALERT_PLACES.map((place) => (
          <React.Fragment key={place.kind}>
            <span className="flex items-center gap-2 border-b border-slate-100 px-2 py-1.5 last:border-b-0">
              <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: place.color }} />
              <span className="min-w-0"><b className="block font-bold text-slate-900">{place.label}</b><span className="block text-[0.68rem] text-slate-700">{place.hint}</span></span>
            </span>
            {ALERT_TYPES.map(([type, typeLabel]) => {
              const kind = `${place.kind}:${type}`;
              const checked = kinds.includes(kind);
              return (
                <button key={kind} type="button" role="menuitemcheckbox" aria-checked={checked} tabIndex={-1} data-keep-menu-open data-alert-kind={kind}
                  aria-label={`${typeLabel} · ${place.label}`} onClick={() => toggle(kind)}
                  className={`grid place-items-center border-b border-slate-100 hover:bg-slate-50 ${gates[type] ? "" : "opacity-40"}`}>
                  <CheckBox checked={checked} />
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      {!gates.arrival || !gates.departure ? (
        <div className="px-2 pb-1.5 text-[0.72rem] text-[#c2410c]">« {gates.arrival ? "Terminé" : "Arrivée"} » est éteint dans le menu : aucun chauffeur ne le reçoit.</div>
      ) : null}
      <div className="flex gap-1.5 px-2 pb-2">
        {[["Tous les avis", () => setKinds(ALL_ALERT_KINDS)], ["Aucun avis", () => setKinds([])]].map(([text, action]) => (
          <button key={text} type="button" role="menuitem" tabIndex={-1} data-keep-menu-open onClick={action}
            className="inline-flex h-7 items-center rounded-[4px] border border-slate-300 bg-white px-2.5 text-[0.72rem] font-extrabold text-slate-700 hover:bg-slate-50">{text}</button>
        ))}
        <button type="button" role="menuitem" tabIndex={-1} data-keep-menu-open
          disabled={!(display.alert_driver_rules || []).some((rule) => rule.key === driver.driver_key)}
          onClick={() => onChange("alert_driver_rules", withDriverRule(display, driver, null))}
          className="ml-auto inline-flex h-7 items-center rounded-[4px] border border-accent bg-white px-2.5 text-[0.72rem] font-extrabold text-accent hover:bg-slate-50 disabled:opacity-40">
          Revenir au défaut
        </button>
      </div>
      <div className="px-2 pb-1.5 text-[0.72rem] text-slate-700">
        {display.alert_others_off ? "Défaut : aucun avis (« Aucun avis pour les autres » est activé)." : "Défaut : les avis choisis dans le menu, comme les autres chauffeurs."}
      </div>
    </div>
  );
}
